import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { rem } from '@mantine/core';
import React, { forwardRef, memo } from 'react';
import styled from 'styled-components';
import { PredefinedIcons } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.types';
import { PredefinedTextVariants } from '@dealstack/frontend/shared/components/fundament/Text/Text.types';
import type {
	ButtonV2SizeValues,
	ButtonV2ThemeValues,
	ButtonV2VariantValues,
	IButtonV2Props,
} from './Button.v2.types';

export enum ButtonV2Variant {
	primary = 'primary',
	secondary = 'secondary',
	tertiary = 'tertiary',
	ghost = 'ghost',
	deletion = 'deletion',
}

export enum ButtonV2Theme {
	light = 'light',
	dark = 'dark',
}

const buttonSizeStyleMapping = {
	xs: `
        height: ${rem(24)};
        border-radius: ${rem(4)};
        gap:${rem(4)};
        padding:${rem(4)};
        ${Icon}.svg {
            width: ${rem(12)};
            height: ${rem(12)};
        }`,
	s: `
        height: ${rem(32)};
        border-radius:${rem(4)};
        gap:${rem(6)};
        padding:${rem(8)};
        ${Icon}.svg {
            width: ${rem(16)};
            height: ${rem(16)};
        }`,
	m: `
        height: ${rem(40)};
        border-radius:${rem(4)};
        gap:${rem(6)};
        padding:${rem(8)};
        ${Icon}.svg {
            width: ${rem(24)};
            height: ${rem(24)};
        }`,
	l: `
        height: ${rem(48)};
        border-radius:${rem(4)};
        gap:${rem(8)};
        padding:${rem(8)};
        ${Icon}.svg {
            width: ${rem(32)};
            height: ${rem(32)};
        }`,
};

type IButtonVariantStyleMapping = {
	[key in `${ButtonV2VariantValues}-${ButtonV2ThemeValues}`]: {
		defaultBorderColor?: string;
		defaultBorderWidth?: {
			[key in ButtonV2SizeValues]: string;
		};
		defaultBgColor: string;
		focusBgColor: string;
		focusOutlineColor?: string;
		hoverBgColor: string;
		hoverBorderColor?: string;
		activeBgColor: string;
		fontColor: string;
		activeFontColor: string;
	};
};

const buttonVariantColorConfigMap: IButtonVariantStyleMapping = {
	[`${ButtonV2Variant.primary}-${ButtonV2Theme.light}` as const]: {
		defaultBgColor: defaultTheme.v2.color.secondary[800],
		focusBgColor: defaultTheme.v2.color.secondary[800],
		focusOutlineColor: defaultTheme.v2.color.secondary[600],
		hoverBgColor: defaultTheme.v2.color.secondary[1000],
		activeBgColor: defaultTheme.v2.color.secondary[1200],
		fontColor: defaultTheme.v2.color.white[500],
		activeFontColor: defaultTheme.v2.color.white[500],
	},
	[`${ButtonV2Variant.primary}-${ButtonV2Theme.dark}` as const]: {
		defaultBgColor: defaultTheme.v2.color.secondary[600],
		focusBgColor: defaultTheme.v2.color.secondary[600],
		focusOutlineColor: defaultTheme.v2.color.secondary[800],
		hoverBgColor: defaultTheme.v2.color.secondary[400],
		activeBgColor: defaultTheme.v2.color.secondary[200],
		fontColor: defaultTheme.v2.color.black[500],
		activeFontColor: defaultTheme.v2.color.black[500],
	},
	[`${ButtonV2Variant.secondary}-${ButtonV2Theme.light}` as const]: {
		defaultBgColor: defaultTheme.v2.color.neutral[300],
		focusBgColor: defaultTheme.v2.color.neutral[400],
		focusOutlineColor: defaultTheme.v2.color.neutral[500],
		hoverBgColor: defaultTheme.v2.color.neutral[500],
		activeBgColor: defaultTheme.v2.color.neutral[1100],
		fontColor: defaultTheme.v2.color.neutral[1100],
		activeFontColor: defaultTheme.v2.color.white[500],
	},
	[`${ButtonV2Variant.secondary}-${ButtonV2Theme.dark}` as const]: {
		defaultBgColor: defaultTheme.v2.color.neutral[1000],
		focusBgColor: defaultTheme.v2.color.neutral[900],
		focusOutlineColor: defaultTheme.v2.color.neutral[800],
		hoverBgColor: defaultTheme.v2.color.neutral[800],
		activeBgColor: defaultTheme.v2.color.neutral[200],
		fontColor: defaultTheme.v2.color.neutral[200],
		activeFontColor: defaultTheme.v2.color.black[500],
	},
	[`${ButtonV2Variant.tertiary}-${ButtonV2Theme.light}` as const]: {
		defaultBorderColor: defaultTheme.v2.color.secondary[800],
		hoverBorderColor: defaultTheme.v2.color.secondary[800],
		defaultBorderWidth: {
			xs: rem(1),
			s: rem(1),
			m: rem(1),
			l: rem(1),
		},
		defaultBgColor: defaultTheme.v2.color.white[500],
		focusBgColor: defaultTheme.v2.color.neutral[300],
		hoverBgColor: defaultTheme.v2.color.neutral[200],
		activeBgColor: defaultTheme.v2.color.neutral[500],
		fontColor: defaultTheme.v2.color.secondary[800],
		activeFontColor: defaultTheme.v2.color.secondary[800],
	},
	[`${ButtonV2Variant.tertiary}-${ButtonV2Theme.dark}` as const]: {
		defaultBorderColor: defaultTheme.v2.color.secondary[600],
		defaultBorderWidth: {
			xs: rem(1),
			s: rem(1),
			m: rem(1),
			l: rem(1),
		},
		defaultBgColor: defaultTheme.v2.color.black[500],
		focusBgColor: defaultTheme.v2.color.neutral[300],
		hoverBorderColor: defaultTheme.v2.color.secondary[800],
		hoverBgColor: defaultTheme.v2.color.neutral[200],
		activeBgColor: defaultTheme.v2.color.neutral[500],
		fontColor: defaultTheme.v2.color.secondary[800],
		activeFontColor: defaultTheme.v2.color.secondary[600],
	},
	[`${ButtonV2Variant.ghost}-${ButtonV2Theme.light}` as const]: {
		defaultBgColor: 'transparent',
		focusBgColor: defaultTheme.v2.color.white[500],
		focusOutlineColor: defaultTheme.v2.color.secondary[400],
		hoverBgColor: defaultTheme.v2.color.secondary[200],
		activeBgColor: defaultTheme.v2.color.secondary[600],
		fontColor: defaultTheme.v2.color.secondary[800],
		activeFontColor: defaultTheme.v2.color.secondary[1200],
	},
	[`${ButtonV2Variant.ghost}-${ButtonV2Theme.dark}` as const]: {
		defaultBgColor: 'transparent',
		focusBgColor: defaultTheme.v2.color.white[500],
		focusOutlineColor: defaultTheme.v2.color.secondary[1000],
		hoverBgColor: defaultTheme.v2.color.secondary[1200],
		activeBgColor: defaultTheme.v2.color.secondary[800],
		fontColor: defaultTheme.v2.color.secondary[600],
		activeFontColor: defaultTheme.v2.color.secondary[200],
	},
	[`${ButtonV2Variant.deletion}-${ButtonV2Theme.light}` as const]: {
		defaultBgColor: defaultTheme.v2.color.error[800],
		focusBgColor: defaultTheme.v2.color.error[800],
		focusOutlineColor: defaultTheme.v2.color.error[800],
		hoverBgColor: defaultTheme.v2.color.error[1000],
		activeBgColor: defaultTheme.v2.color.error[1200],
		fontColor: defaultTheme.v2.color.white[500],
		activeFontColor: defaultTheme.v2.color.white[500],
	},
	[`${ButtonV2Variant.deletion}-${ButtonV2Theme.dark}` as const]: {
		defaultBgColor: defaultTheme.v2.color.error[800],
		focusBgColor: defaultTheme.v2.color.error[800],
		focusOutlineColor: defaultTheme.v2.color.error[800],
		hoverBgColor: defaultTheme.v2.color.error[400],
		activeBgColor: defaultTheme.v2.color.error[200],
		fontColor: defaultTheme.v2.color.black[500],
		activeFontColor: defaultTheme.v2.color.black[500],
	},
};

const textVariant = {
	xs: 'sans/09/600',
	s: 'sans/11/600',
	m: 'sans/14/600',
	l: 'sans/17/600',
};

const iconPadding = {
	xs: `${rem(4)}`,
	s: `${rem(8)}`,
	m: `${rem(8)}`,
	l: `${rem(8)}`,
};

const focussedButtonOutlineWidth = {
	xs: `${rem(2)}`,
	s: `${rem(2)}`,
	m: `${rem(2)}`,
	l: `${rem(2)}`,
};

type IIconPadding = keyof typeof iconPadding;

const Button = forwardRef<HTMLButtonElement, IButtonV2Props>(
	(
		{ className, buttonType = 'button', children, size, icon, rightIconOverride, ...props },
		ref
	) => {
		const isText = typeof children === 'string';
		const isIconPredefined = typeof icon === 'string';
		const isRightIconPredefined = typeof rightIconOverride === 'string';

		let IconComponent: React.ReactNode = null;
		if (icon) {
			IconComponent = isIconPredefined ? <Icon variant={icon as PredefinedIcons} /> : icon;
		}

		let RightIconComponent: React.ReactNode = null;
		if (rightIconOverride) {
			RightIconComponent = isRightIconPredefined ? (
				<Icon variant={rightIconOverride as PredefinedIcons} />
			) : (
				rightIconOverride
			);
		}

		const textContent = isText ? (
			<Text
				key={JSON.stringify(size)}
				variant={textVariant[size] as PredefinedTextVariants}
				disableVariantMapping
				capitalizeFirst
			>
				{children}
			</Text>
		) : (
			children
		);

		const rightIconContent =
			props.rightIcon && (rightIconOverride ? RightIconComponent : IconComponent);

		return (
			<button type={buttonType} className={className} {...props} ref={ref}>
				<Container>
					{props.leftIcon && <LeftIconWrapper>{IconComponent}</LeftIconWrapper>}
					{textContent}
					{rightIconContent && <RightIconWrapper>{rightIconContent}</RightIconWrapper>}
				</Container>
			</button>
		);
	}
);

Button.displayName = 'Button';

export const LeftIconWrapper = styled.div``;
const RightIconWrapper = styled.div``;
const Container = styled.div`
	display: flex;
	flex-grow: 1;
	flex-direction: row;
	justify-content: center;
	align-items: center;
	align-self: center;
`;
const Memoized = memo(Button);
const Styled = styled(Memoized)`
	cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
	outline: none;
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: center;
	text-align: left;
	box-sizing: border-box;

	${LeftIconWrapper} {
		display: contents;

		& > * {
			margin-right: ${({ size }) => iconPadding[size as IIconPadding]};
		}
	}

	${RightIconWrapper} {
		display: contents;

		& > * {
			margin-left: ${({ size }) => iconPadding[size as IIconPadding]};
		}
	}

	${({ size }) => buttonSizeStyleMapping[size]};

	${({ variant, size, buttonTheme }) => `
        --opacity: 0.32;
        --font-color: ${buttonVariantColorConfigMap[`${variant}-${buttonTheme}`].fontColor};

        --border-style: solid ${buttonVariantColorConfigMap[`${variant}-${buttonTheme}`].defaultBorderWidth?.[size]}
        ${buttonVariantColorConfigMap[`${variant}-${buttonTheme}`].defaultBorderColor};

        --hover-border-style: solid ${
			buttonVariantColorConfigMap[`${variant}-${buttonTheme}`].defaultBorderWidth?.[size]
		} ${buttonVariantColorConfigMap[`${variant}-${buttonTheme}`].hoverBorderColor};

        color: var(--font-color);
        background-color: ${buttonVariantColorConfigMap[`${variant}-${buttonTheme}`].defaultBgColor};
        border: var(--border-style);

        &:focus {
            background-color: ${buttonVariantColorConfigMap[`${variant}-${buttonTheme}`].focusBgColor};
            outline: ${
				buttonVariantColorConfigMap[`${variant}-${buttonTheme}`].defaultBorderWidth
					? `none`
					: `solid  ${buttonVariantColorConfigMap[`${variant}-${buttonTheme}`].focusOutlineColor} ${
							focussedButtonOutlineWidth[size]
						}`
			};
        }

        &:hover {
            background-color: ${buttonVariantColorConfigMap[`${variant}-${buttonTheme}`].hoverBgColor};
            border: var(--hover-border-style);
        }

        &:active {
            background-color: ${buttonVariantColorConfigMap[`${variant}-${buttonTheme}`].activeBgColor};
            border: var(--border-style);
            color: ${buttonVariantColorConfigMap[`${variant}-${buttonTheme}`].activeFontColor};

            ${Icon}.svg {
                color: ${buttonVariantColorConfigMap[`${variant}-${buttonTheme}`].activeFontColor};
                fill:${buttonVariantColorConfigMap[`${variant}-${buttonTheme}`].activeFontColor};
                object-fit: contain;
            }
        }

        &:disabled {
            background-color: ${buttonVariantColorConfigMap[`${variant}-${buttonTheme}`].defaultBgColor};
            opacity: var(--opacity);
            border: var(--border-style);
        }

        ${Icon}.svg {
            fill: var(--font-color);
            color: var(--font-color);
            object-fit: contain;
        }

    `};
`;

export { Styled as Button };
